import {
  Bar,
  Cell,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { IScore } from "../../types/insights";
import { chartTooltipStyles } from "../ChartTooltip";

const levels = [
  { name: "Building", min: 0, max: 30 },
  { name: "Foundation", min: 31, max: 80 },
  { name: "Advanced", min: 81, max: 94 },
  { name: "Leading", min: 95, max: 100 },
];

const levelTickFormatter = (tick: number) => {
  const level = levels.find((l) => tick >= l.min && tick <= l.max);
  return level ? level.name : "";
};

const customBarColor = (stage: string) => {
  switch (stage) {
    case "Compliant":
      return "#fffaef";
    case "Discovering":
      return "#fef4e0";
    case "Committing":
      return "#feefd2";
    case "Championed":
      return "#feefd2";
    case "Leading":
      return "#fde5b5";
    default:
      return "#fde5b5";
  }
};

export const maturityGraphStages = [
  { name: "Compliant", min: 0, max: 20 },
  { name: "Discovering", min: 20, max: 45 },
  { name: "Committing", min: 45, max: 65 },
  { name: "Championed", min: 65, max: 85 },
  { name: "Leading", min: 85, max: 101 }, // Max of 101 here in case someone scores 100
];

const getGraphData = (score: number) => {
  return maturityGraphStages.map((stage) => ({
    stage: stage.name,
    min: stage.min,
    max: stage.max,
    range: stage.max - 0,
    dotPosition: score >= stage.min && score < stage.max ? stage.max : null,
  }));
};

type MaturityGraphProps = {
  overallScore: number;
  verifiedScores?: IScore[];
  // When provided, overrides whether the unverified score dot is shown (so the
  // dashboard can hide it unless action items changed since the last verification).
  showUnverified?: boolean;
};

export const MaturityGraph = ({
  overallScore,
  verifiedScores = [],
  showUnverified,
}: MaturityGraphProps): React.ReactElement => {
  const data = getGraphData(overallScore);

  // Find which stage a score belongs to and return dot position
  const getScoreDot = (score: number) => {
    const stage = maturityGraphStages.find(
      (s) => score >= s.min && score < s.max,
    );
    return stage ? { stage: stage.name, y: stage.max } : null;
  };

  // Build verified score dots (gold rings) with opacity fading for older scores
  const verifiedDots = verifiedScores
    .map((s, index) => {
      const dot = getScoreDot(s.score);
      if (!dot) return null;
      // Opacity: most recent = 1, older ones fade out (min 0.15)
      const opacity = verifiedScores.length > 1
        ? 0.15 + (0.85 * index) / (verifiedScores.length - 1)
        : 1;
      return { ...dot, score: s.score, createdAt: s.createdAt, opacity };
    })
    .filter((d): d is { stage: string; y: number; score: number; createdAt: Date; opacity: number } => d !== null);

  // Unverified score dot (blue ring) — only if different from latest verified
  const latestVerified = verifiedScores.length > 0
    ? verifiedScores[verifiedScores.length - 1].score
    : null;
  const showUnverifiedDot =
    showUnverified ??
    (latestVerified === null || overallScore !== latestVerified);
  const unverifiedDot = showUnverifiedDot ? getScoreDot(overallScore) : null;

  // Add scoreLine values to chart data for connecting line (at bar top position)
  const allDots = [
    ...verifiedDots.map((d) => ({ stage: d.stage, y: d.y })),
    ...(unverifiedDot ? [{ stage: unverifiedDot.stage, y: unverifiedDot.y }] : []),
  ];

  const chartData = data.map((entry) => {
    const dotInStage = allDots.find((d) => d.stage === entry.stage);
    return {
      ...entry,
      scoreLine: dotInStage ? dotInStage.y : undefined,
    };
  });

  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart
        data={chartData}
        margin={{
          bottom: 30,
        }}
      >
        <XAxis
          dataKey="stage"
          axisLine={{ stroke: "#E9E9E9" }}
          tick={{
            fontSize: "9px",
          }}
        />
        <YAxis
          domain={[0, 100]}
          tickFormatter={levelTickFormatter}
          type="number"
          tickSize={0}
          ticks={[]}
          hide={true}
          interval={0}
          axisLine={{ stroke: "#E9E9E9" }}
          tick={{ fontSize: "13px", fill: "#374151" }}
        />
        <Tooltip
          cursor={false}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const stage = payload[0]?.payload?.stage;
            const stageVerified = verifiedDots.filter((d) => d.stage === stage);
            const stageUnverified = unverifiedDot?.stage === stage ? overallScore : null;
            if (stageVerified.length === 0 && stageUnverified === null) return null;
            return (
              <div style={{
                ...chartTooltipStyles.contentStyle,
                whiteSpace: "nowrap",
              }}>
                <div style={chartTooltipStyles.labelStyle}>{stage}</div>
                {stageVerified.map((d, i) => (
                  <div key={i} style={{ ...chartTooltipStyles.itemStyle, color: "#fff" }}>
                    {d.score}% — {new Date(d.createdAt).toLocaleString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                ))}
                {stageUnverified !== null && (
                  <div style={{ ...chartTooltipStyles.itemStyle, color: "#9e9e9e" }}>
                    {stageUnverified}% — Unverified (current)
                  </div>
                )}
              </div>
            );
          }}
        />
        <Bar dataKey="range" fill="#fde5b5" radius={[4, 4, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={customBarColor(entry.stage)} />
          ))}
        </Bar>
        <Line
          type="monotone"
          dataKey="scoreLine"
          stroke="#d0d0d0"
          strokeWidth={1.5}
          dot={false}
          connectNulls={true}
          isAnimationActive={false}
        />
        {verifiedDots.map((dot, index) => (
          <ReferenceDot
            key={`verified-${index}`}
            x={dot.stage}
            y={dot.y}
            r={4}
            fill="white"
            stroke={`rgba(250, 170, 10, ${dot.opacity})`}
            strokeWidth={2.5}
            ifOverflow="visible"
            isFront={true}
          />
        ))}
        {unverifiedDot && (
          <ReferenceDot
            key="unverified"
            x={unverifiedDot.stage}
            y={unverifiedDot.y}
            r={4}
            fill="white"
            stroke="#9e9e9e"
            strokeWidth={2.5}
            ifOverflow="visible"
            isFront={true}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
